import { FC, useState } from 'react'

import { message } from 'antd'
import styled, { useTheme } from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'

import { PrimaryInput } from 'src/Components/PrimaryInput'
import {
  getAuthEmail,
  getIsAuthTypeLoading,
} from 'src/Redux/auth-process/authTypeSlice/selectors'
import { checkUserEmailToSignIn } from 'src/Redux/auth-process/authTypeSlice/api-actions'
import { USER_NOT_FOUND_TYPE } from 'src/Redux/auth-process/authTypeSlice'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { ApiError } from 'src/Redux/types'
import { FullWideNotification } from 'src/Components/FullWideNotification'
import { sendQuickLoginLink } from 'src/Redux/auth-process/emailPasswordSlice/api-actions'
import { useQuickLoginUTMParameters } from 'src/Hooks/useQuickLoginUTMParameters'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { GoogleAuthButton } from 'src/Components/Buttons/GoogleAuthButton'
import { TextLinkButton } from 'src/Components/Buttons/TextLinkButton'
import { OrDivider } from 'src/Components/OrDivider'
import { initiateGoogleAuth } from 'src/Utils/googleAuth'
import { useGoogleAuthError } from 'src/Hooks/useGoogleAuthError'
import { variables } from 'src/Styled/variables'
import { PublicRoutesEnum } from 'src/Router/PublicComponentsNewUI'

import { schemaForInitialLogin } from './schema'

const Title = styled.p`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;

  @media (min-width: 576px) {
    font-size: 28px;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const StyledCTAButton = styled(CTAButton)`
  width: 100%;
`

const GoogleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ErrorRow = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.color.mediumRed};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  text-align: left;
`

const SignUpLink = styled(TextLinkButton)`
  border-bottom: none;
  color: ${({ theme }) => theme.color.brandGreen};
  text-decoration: underline;
`

const Footer = styled.div`
  display: flex;
  gap: 4px;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`

const FooterText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

interface FormState {
  email: string
}

export const LoginInitial: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const loading = useAppSelector(getIsAuthTypeLoading)
  const [messageApi, contextHolder] = message.useMessage()
  const defaultEmail = useAppSelector(getAuthEmail)
  const utmParameters = useQuickLoginUTMParameters()
  const googleAuthError = useGoogleAuthError()
  const [emailNotFound, setEmailNotFound] = useState(false)

  const { control, handleSubmit } = useForm<FormState>({
    defaultValues: {
      email: defaultEmail,
    },
    resolver: yupResolver(schemaForInitialLogin),
  })

  const onSubmit = ({ email: submittedEmail }: FormState): void => {
    setEmailNotFound(false)
    void dispatch(checkUserEmailToSignIn(submittedEmail))
      .unwrap()
      .then((res) => {
        if (res.type === USER_NOT_FOUND_TYPE) {
          setEmailNotFound(true)
          return
        }
        if (res.type === 'withoutSignup') {
          void dispatch(
            sendQuickLoginLink({
              email: submittedEmail,
              utmParameters,
            }),
          )
        }
      })
      .catch((error: ApiError) => {
        if (error.message === 'Curator in blacklist') {
          void messageApi.open({
            content: (
              <FullWideNotification message='Sorry, our platform is no longer available for users from your country' />
            ),
            duration: 3,
            style: { backgroundColor: theme.color.mediumRed },
            icon: null,
          })
        }
      })
  }

  const handleSignUp = (): void => {
    navigate(PublicRoutesEnum.SignUpChooseRole)
  }

  const handleSignUpArtist = (): void => {
    navigate(
      `${PublicRoutesEnum.CreateSpotifyCampaign}${PublicRoutesEnum.SignUpArtist}`,
    )
  }

  return (
    <>
      {contextHolder}
      <Title>Welcome back!</Title>
      <Content>
        <GoogleBlock>
          <GoogleAuthButton
            label='Log in with Google'
            onClick={() => initiateGoogleAuth({})}
          />
          {googleAuthError && <ErrorRow>{googleAuthError}</ErrorRow>}
        </GoogleBlock>
        <OrDivider />
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name='email'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PrimaryInput
                {...field}
                onChange={(e) => {
                  setEmailNotFound(false)
                  field.onChange(e)
                }}
                placeholder='Email'
                error={error?.message}
                type='email'
                autoFocus
              />
            )}
          />
          <StyledCTAButton type='submit' loading={loading}>
            Continue
          </StyledCTAButton>
        </Form>
        {emailNotFound && (
          <ErrorRow>
            {`We couldn't find this email. Check your spelling or `}
            <SignUpLink type='button' onClick={handleSignUpArtist}>
              Sign up
            </SignUpLink>
          </ErrorRow>
        )}
      </Content>
      <Footer>
        <FooterText>New to SoundCampaign?</FooterText>
        <TextLinkButton type='button' onClick={handleSignUp}>
          Sign up
        </TextLinkButton>
      </Footer>
    </>
  )
}
